/**
 * Align — linking existing work to an objective.
 *
 * The mapping write itself is `createMapping` (the same call the New item
 * form makes); this file only decides which objectives can be picked and
 * turns a refused write into a sentence a person can act on.
 */

import { createMapping, type ObjectivePriorityMapping, type StrategicObjective } from '@/lib/strategyAlignment';

/** Objectives that can take new work: the ones still being pursued. */
export function linkableObjectives(objectives: StrategicObjective[]): StrategicObjective[] {
  return objectives.filter((o) => o.status === 'active');
}

/** Plain-language reason a link was refused. */
export function linkErrorMessage(err: unknown): string {
  const code = typeof err === 'object' && err !== null ? (err as { code?: unknown }).code : undefined;
  if (code === '42501') return "You don't have permission to link work to objectives.";
  if (code === '23514') return 'That goal is not under this objective.';
  return 'Could not link this item. Try again.';
}

export type LinkResult = { ok: true } | { ok: false; message: string };

/** Link one item to one objective. Same default strength as the New item form; the item page lets the person say. */
export async function linkItemToObjective(args: {
  itemId: string;
  objectiveId: string;
  userId: string;
  strength?: ObjectivePriorityMapping['alignmentStrength'];
  /** A goal under this objective (the database refuses any other). */
  goalId?: string;
}): Promise<LinkResult> {
  try {
    await createMapping(
      {
        objectiveId: args.objectiveId,
        priorityId: args.itemId,
        alignmentStrength: args.strength ?? 'primary',
        ...(args.goalId ? { goalId: args.goalId } : {}),
        createdBy: args.userId,
      },
      args.userId,
    );
    return { ok: true };
  } catch (err) {
    return { ok: false, message: linkErrorMessage(err) };
  }
}
